// frontend/src/components/MostPopularTrailers.jsx
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useHorizontalDragScroll } from "./useHorizontalDragScroll";
import { youtubeThumb } from "../services/trailers";
import TrailerModal from "./TrailerModal";

export default function MostPopularTrailers({
  items = [],
  variant = "discover",
  className = "",
}) {
  const safeItems = Array.isArray(items) ? items : [];
  const { ref, bind } = useHorizontalDragScroll({ dragThresholdPx: 7 });

  // Modal control (uses your existing TrailerModal)
  const [open, setOpen] = useState(false);
  const [activeTrailer, setActiveTrailer] = useState(null);

  const openTrailer = (t) => {
    if (!t?.youtubeId) return;
    setActiveTrailer({
      youtubeId: t.youtubeId,
      title: t?.title || t?.name || "Trailer",
      id: t?.id,
      animeId: t?.animeId,
    });
    setOpen(true);
  };

  const closeTrailer = () => {
    setOpen(false);
    // Optional: clear after close (keeps next open clean)
    // setActiveTrailer(null);
  };

  const scrollByAmount = (dir) => {
    const el = ref.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.9) * dir;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  // Floating chevrons only (no circle/background/border)
  // - big icon
  // - generous invisible hit area
  // - subtle depth via drop-shadow
  const arrowBtn =
    "pointer-events-auto inline-flex items-center justify-center " +
    "h-12 w-12 2xl:h-14 2xl:w-14 " +
    "bg-transparent border-0 rounded-none p-0 " +
    "text-white/70 hover:text-white " +
    "drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)] " +
    "transition duration-200";

  const DESKTOP_XL = 1280;
  const isDesktopXL = () =>
    typeof window !== "undefined" && window.innerWidth >= DESKTOP_XL;

  const cardWidthClass = useMemo(() => {
    if (variant === "discover") {
      return "w-[320px] sm:w-[380px] md:w-[420px]";
    }
    return "w-[320px] sm:w-[380px] md:w-[420px]";
  }, [variant]);

  return (
    <div className={["relative overflow-visible", className].join(" ")}>
      {/* Use your built TrailerModal */}
      <TrailerModal open={open} onClose={closeTrailer} trailer={activeTrailer} />

      {/* Desktop arrows (placed OUTSIDE the rail, intentional offsets) */}
      <div className="hidden xl:flex absolute inset-y-0 -left-12 2xl:-left-16 items-center z-20 pointer-events-none">
        <button
          type="button"
          onClick={() => scrollByAmount(-1)}
          className={arrowBtn}
          aria-label="Scroll trailers left"
          title="Scroll left"
        >
          <ChevronLeft className="h-6 w-6 2xl:h-8 2xl:w-8 -translate-x-[2px]" />
        </button>
      </div>

      <div className="hidden xl:flex absolute inset-y-0 -right-12 2xl:-right-16 items-center z-20 pointer-events-none">
        <button
          type="button"
          onClick={() => scrollByAmount(1)}
          className={arrowBtn}
          aria-label="Scroll trailers right"
          title="Scroll right"
        >
          <ChevronRight className="h-6 w-6 2xl:h-8 2xl:w-8 translate-x-[2px]" />
        </button>
      </div>

      {/* Rail */}
      <div
        ref={ref}
        className={[
          "hide-scroll flex overflow-x-auto scroll-smooth min-h-0",
          "gap-3 sm:gap-4",
          "pb-3 sm:pb-4",
          "snap-x snap-mandatory",
          "[scrollbar-width:none] [-ms-overflow-style:none]",
          "touch-pan-x",
          "cursor-grab select-none",
          "w-full",
          "xl:cursor-auto",
        ].join(" ")}
        onClickCapture={(e) => {
          if (isDesktopXL()) return;
          bind.onClickCapture?.(e);
        }}
        onPointerDown={(e) => {
          if (isDesktopXL()) return;
          bind.onPointerDown?.(e);
        }}
        onPointerMove={(e) => {
          if (isDesktopXL()) return;
          bind.onPointerMove?.(e);
        }}
        onPointerUp={(e) => {
          if (isDesktopXL()) return;
          bind.onPointerUp?.(e);
        }}
        onPointerCancel={(e) => {
          if (isDesktopXL()) return;
          bind.onPointerCancel?.(e);
        }}
        onPointerLeave={(e) => {
          if (isDesktopXL()) return;
          bind.onPointerLeave?.(e);
        }}
        onWheel={(e) => {
          bind.onWheel?.(e);
        }}
      >
        {safeItems.map((t, idx) => {
          const key = t?.id || t?.key || t?.TrailerID || idx;
          const title = t?.title || t?.name || "Trailer";
          const hasYouTube = Boolean(t?.youtubeId);
          const thumb = hasYouTube ? youtubeThumb(t.youtubeId) : "";

          return (
            <div key={key} className={`${cardWidthClass} flex-shrink-0 snap-start`}>
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
                {/* Click target */}
                <button
                  type="button"
                  onClick={() => openTrailer(t)}
                  disabled={!hasYouTube}
                  className={[
                    "relative aspect-[16/9] w-full overflow-hidden",
                    "bg-black/40",
                    "text-left",
                    hasYouTube ? "cursor-pointer" : "cursor-not-allowed opacity-60",
                  ].join(" ")}
                  aria-label={hasYouTube ? `Play trailer: ${title}` : "Trailer unavailable"}
                  title={hasYouTube ? "Play trailer" : "Trailer unavailable"}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={title}
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      draggable={false}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}

                  {/* overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/5" />

                  {/* play hint */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full border border-white/25 bg-black/35 backdrop-blur-sm flex items-center justify-center">
                      <span className="text-[18px] text-white/90 ml-[2px]">▶</span>
                    </div>
                  </div>
                </button>

                <div className="p-3">
                  <p className="text-[10px] tracking-[0.32em] uppercase text-white/45">
                    Watch Trailer
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white/85 line-clamp-1">
                    {title}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .hide-scroll::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
